import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";

const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

const CURRENCIES = {
  INR: { code: "INR", symbol: "₹", rate: 1.0, label: "India (₹)", flag: "🇮🇳" },
  AED: { code: "AED", symbol: "AED", rate: 0.044, label: "UAE (AED)", flag: "🇦🇪" },
  SAR: { code: "SAR", symbol: "SAR", rate: 0.045, label: "Saudi Arabia (SAR)", flag: "🇸🇦" },
  QAR: { code: "QAR", symbol: "QAR", rate: 0.044, label: "Qatar (QAR)", flag: "🇶🇦" },
  OMR: { code: "OMR", symbol: "OMR", rate: 0.0046, label: "Oman (OMR)", flag: "🇴🇲" },
  KWD: { code: "KWD", symbol: "KWD", rate: 0.0037, label: "Kuwait (KWD)", flag: "🇰🇼" },
  BHD: { code: "BHD", symbol: "BHD", rate: 0.0045, label: "Bahrain (BHD)", flag: "🇧🇭" },
  USD: { code: "USD", symbol: "$", rate: 0.012, label: "USD ($)", flag: "🇺🇸" },
};

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [currency, setCurrency] = useState(
    () => localStorage.getItem("bc_currency") || "INR"
  );
  const [cart, setCart] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("bc_cart") || "[]");
    } catch {
      return [];
    }
  });
  const [wishlist, setWishlist] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, []);

  // --- Auth ---
  useEffect(() => {
    const token = localStorage.getItem("bc_token");
    if (!token) {
      setAuthChecked(true);
      return;
    }
    api
      .get("/auth/me")
      .then(({ data }) => {
        setUser(data);
        setWishlist(data.wishlist || []);
      })
      .catch(() => localStorage.removeItem("bc_token"))
      .finally(() => setAuthChecked(true));
  }, []);

  const loginSuccess = useCallback((data) => {
    localStorage.setItem("bc_token", data.token);
    setUser(data.user);
    setWishlist(data.user.wishlist || []);
    setAuthModalOpen(false);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("bc_token");
    setUser(null);
    setWishlist([]);
    toast.success("Logged out");
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      setWishlist(data.wishlist || []);
      return data;
    } catch {
      return null;
    }
  }, []);

  // --- Currency ---
  useEffect(() => {
    localStorage.setItem("bc_currency", currency);
  }, [currency]);

  const cur = CURRENCIES[currency];
  const formatPrice = useCallback(
    (inr) => {
      const c = CURRENCIES[currency];
      const val = inr * c.rate;
      if (c.code === "INR") return `₹${Math.round(val).toLocaleString("en-IN")}`;
      return `${c.symbol} ${val.toFixed(2)}`;
    },
    [currency]
  );

  // --- Cart ---
  useEffect(() => {
    localStorage.setItem("bc_cart", JSON.stringify(cart));
  }, [cart]);

  const addToCart = useCallback((product, size, color, quantity = 1, logo = true) => {
    setCart((prev) => {
      const idx = prev.findIndex(
        (i) => i.product.id === product.id && i.size === size && i.color === color && i.logo === logo
      );
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], quantity: copy[idx].quantity + quantity };
        return copy;
      }
      return [...prev, { product, size, color, quantity, logo }];
    });
    toast.success(`${product.name} added to bag`);
    setCartOpen(true);
  }, []);

  const updateQty = useCallback((index, delta) => {
    setCart((prev) =>
      prev
        .map((item, i) =>
          i === index ? { ...item, quantity: Math.max(1, item.quantity + delta) } : item
        )
        .filter((i) => i.quantity > 0)
    );
  }, []);

  const removeFromCart = useCallback((index) => {
    setCart((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const cartTotalInr = cart.reduce((sum, i) => sum + i.product.price * i.quantity, 0);
  const cartCount = cart.reduce((sum, i) => sum + i.quantity, 0);

  // --- Wishlist ---
  const toggleWishlist = useCallback(
    async (productId) => {
      if (!user) {
        setAuthModalOpen(true);
        toast.info("Please login to save favourites");
        return;
      }
      try {
        const { data } = await api.post(`/wishlist/${productId}`);
        setWishlist(data.wishlist);
        toast.success(data.added ? "Saved to wishlist" : "Removed from wishlist");
      } catch {
        toast.error("Could not update wishlist");
      }
    },
    [user]
  );

  const value = {
    user,
    authChecked,
    loginSuccess,
    logout,
    refreshUser,
    currency,
    setCurrency,
    currencies: CURRENCIES,
    cur,
    formatPrice,
    cart,
    addToCart,
    updateQty,
    removeFromCart,
    clearCart,
    cartTotalInr,
    cartCount,
    wishlist,
    toggleWishlist,
    cartOpen,
    setCartOpen,
    authModalOpen,
    setAuthModalOpen,
    settings,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}
